/**
 * FoodPulseJourneyLanding — one dynamic landing per user journey.
 *
 * The SEO audit's core structural recommendation is to consolidate every
 * FoodPulse surface around five user journeys (Discover · Understand ·
 * Nearby · Share · Cook). Phase 5 built the top-of-home bands; this
 * page is the dedicated destination each band links into.
 *
 * URL: /food-pulse/journeys/:key   (discover|understand|nearby|share|cook)
 *
 * Each landing pulls:
 *   • Hero (curated blurb + primary CTA)
 *   • Cornerstone guides filtered to this journey  (/api/food-pulse/guides/journey/:key)
 *   • Journey-specific tool row (Deals map, Scanner, Pantry, Urban Harvest…)
 *   • Contextual directory slice (Deals nearby / Businesses / Recipes)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  Compass, ScanLine, MapPin, Recycle, Utensils, ArrowRight, BookOpen,
  Sparkles, Store, ShoppingBasket, Loader2,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import FoodPulseTopNav from "@/components/FoodPulseTopNav";
import FoodPulseJourneyNav from "@/components/FoodPulseJourneyNav";
import FeatureStatusBadge from "@/components/FeatureStatusBadge";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";

const JOURNEYS = {
  discover: {
    key: "discover",
    label: "Discover",
    tagline: "Find every food, farm, and vendor in your city.",
    intro:
      "Browse the FoodPulse encyclopedia of everyday foods, small farms, " +
      "and neighborhood vendors. Every entry is source-linked and reviewed.",
    accent: "emerald",
    Icon: Compass,
    tools: [
      { to: "/food-pulse/food",       label: "Food encyclopedia",     Icon: BookOpen,        blurb: "Every fruit, vegetable, grain, and staple with storage, allergens, and seasonality." },
      { to: "/food-pulse/businesses", label: "Businesses directory", Icon: Store,           blurb: "Small vendors, farmers, wholesalers — filter by category or claim your listing." },
      { to: "/food-pulse/cities",     label: "City hubs",             Icon: MapPin,          blurb: "Local hubs with news, events, deals, and farmers markets by city." },
    ],
  },
  understand: {
    key: "understand",
    label: "Understand",
    tagline: "Read the label. Trust the food.",
    intro:
      "Cornerstone guides plus the FoodPulse Label Scanner help you decode " +
      "date labels, allergens, and nutrition facts — with sources cited.",
    accent: "sky",
    Icon: ScanLine,
    tools: [
      { to: "/food-pulse/label-scan", label: "Label Scanner",        Icon: ScanLine,        blurb: "Snap a Nutrition Facts panel. Big-9 allergens auto-flagged, methodology transparent." },
      { to: "/food-pulse/recalls",    label: "Recalls & alerts",     Icon: Sparkles,        blurb: "Live FDA/USDA recall feed with plain-English summaries." },
      { to: "/food-pulse/methodology",label: "Methodology & sources",Icon: BookOpen,        blurb: "How we source, review, and correct every article on the site." },
    ],
  },
  nearby: {
    key: "nearby",
    label: "Nearby",
    tagline: "The best deal near you, right now.",
    intro:
      "GPS-aware deal feed, participating shops, and neighborhood pantries — " +
      "every listing timestamped and locality-verified.",
    accent: "amber",
    Icon: MapPin,
    tools: [
      { to: "/food-pulse/deals",      label: "Deals near you",       Icon: Sparkles,        blurb: "Auto-sorted by distance, freshness, and community score." },
      { to: "/food-pulse/businesses", label: "Shops & farms",        Icon: Store,           blurb: "Full directory with hours, deals, and community photos." },
      { to: "/food-pulse/charities",  label: "Pantries & food banks",Icon: ShoppingBasket,  blurb: "1,100+ pantries and community fridges you can search by ZIP or city." },
    ],
  },
  share: {
    key: "share",
    label: "Share",
    tagline: "Reduce waste. Feed your neighborhood.",
    intro:
      "Backyard harvest sharing, safe donation, composting, and gleaning — " +
      "every guide vetted by a farmer, dietitian, or food-safety specialist.",
    accent: "rose",
    Icon: Recycle,
    tools: [
      { to: "/food-pulse/urban-harvest", label: "Urban Harvest",        Icon: Recycle,      blurb: "Register your fruit tree. Choose your sharing mode every season." },
      { to: "/food-pulse/donate",        label: "Donate food",          Icon: ShoppingBasket, blurb: "Route surplus safely with the Bill Emerson checklist." },
      { to: "/food-pulse/volunteer",     label: "Volunteer signup",     Icon: Sparkles,     blurb: "Get matched to a nearby gleaning run, delivery, or pantry shift." },
    ],
  },
  cook: {
    key: "cook",
    label: "Cook",
    tagline: "Use what you have. Waste nothing.",
    intro:
      "Recipes indexed by what's already in your pantry, plus freezer and " +
      "leftover playbooks reviewed by a food-safety specialist.",
    accent: "violet",
    Icon: Utensils,
    tools: [
      { to: "/food-pulse/recipes",    label: "Recipe discovery",     Icon: Utensils,        blurb: "Filter by ingredient, prep time, or diet — with source links to every method." },
      { to: "/food-pulse/pantry",     label: "Smart Pantry",         Icon: ShoppingBasket,  blurb: "Track what you have, get expiry pings, and cook before you waste." },
      { to: "/food-pulse/receipts",   label: "Receipt Scanner",      Icon: ScanLine,        blurb: "Snap a receipt to auto-fill your pantry in seconds." },
    ],
  },
};

const ACCENT_STYLES = {
  emerald: { hero: "bg-emerald-50 text-emerald-900 border-emerald-100", chip: "bg-emerald-600 text-white", ring: "ring-emerald-200" },
  sky:     { hero: "bg-sky-50 text-sky-900 border-sky-100",             chip: "bg-sky-600 text-white",     ring: "ring-sky-200" },
  amber:   { hero: "bg-amber-50 text-amber-900 border-amber-100",       chip: "bg-amber-600 text-white",   ring: "ring-amber-200" },
  rose:    { hero: "bg-rose-50 text-rose-900 border-rose-100",          chip: "bg-rose-600 text-white",    ring: "ring-rose-200" },
  violet:  { hero: "bg-violet-50 text-violet-900 border-violet-100",    chip: "bg-violet-600 text-white",  ring: "ring-violet-200" },
};

export default function FoodPulseJourneyLanding() {
  const { key } = useParams();
  const journey = JOURNEYS[key];
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!journey) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await http.get(`/food-pulse/guides/journey/${journey.key}`);
        if (!cancelled) setGuides(r.data?.items || []);
      } catch {
        if (!cancelled) setGuides([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [journey]);

  const crumb = useMemo(() => (journey ? [
    { name: "FoodPulse",  url: "https://foodpulse.net/food-pulse" },
    { name: journey.label, url: `https://foodpulse.net/food-pulse/journeys/${journey.key}` },
  ] : []), [journey]);

  if (!journey) return <Navigate to="/food-pulse" replace />;

  const s = ACCENT_STYLES[journey.accent];
  const { Icon } = journey;

  return (
    <div className="min-h-screen bg-white" data-testid={`fp-journey-landing-${journey.key}`}>
      <SeoHead
        section="food-pulse"
        title={`${journey.label} — ${journey.tagline}`}
        description={journey.intro}
      />
      <JsonLd data={breadcrumbSchema(crumb)} />
      <FoodPulseTopNav />
      <FoodPulseJourneyNav active={journey.key} />

      {/* Hero */}
      <section className={`border-b ${s.hero}`} data-testid={`fp-journey-hero-${journey.key}`}>
        <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-10 sm:py-14">
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest">
            <Icon className="h-3.5 w-3.5" /> FoodPulse · {journey.label}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl">{journey.tagline}</h1>
          <p className="max-w-2xl text-sm text-slate-700 sm:text-base">{journey.intro}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {journey.tools.slice(0, 2).map((t) => (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`fp-journey-cta-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`inline-flex items-center gap-1.5 rounded-full ${s.chip} px-4 py-2 text-xs font-semibold sm:text-sm`}
              >
                <t.Icon className="h-3.5 w-3.5" /> {t.label} <ArrowRight className="h-3 w-3" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Tools row */}
      <section className="mx-auto max-w-5xl px-4 py-8" data-testid={`fp-journey-tools-${journey.key}`}>
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
          <Sparkles className="h-3.5 w-3.5" /> Tools in this journey
        </div>
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {journey.tools.map((t) => (
            <li key={t.to}>
              <Link
                to={t.to}
                data-testid={`fp-journey-tool-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-emerald-300 hover:shadow-sm`}
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <t.Icon className="h-4 w-4 text-slate-500" /> {t.label}
                </span>
                <span className="mt-1 flex-1 text-xs text-slate-600">{t.blurb}</span>
                <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                  Open <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      {/* Cornerstone guides slice */}
      <section className="mx-auto max-w-5xl px-4 pb-10" data-testid={`fp-journey-guides-${journey.key}`}>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500">
            <BookOpen className="h-3.5 w-3.5" /> Cornerstone guides
          </div>
          <Link
            to="/food-pulse/guides"
            className="text-xs font-semibold text-emerald-700 hover:underline"
            data-testid="fp-journey-guides-see-all"
          >
            All guides →
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading guides…
          </div>
        ) : guides.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
            More guides for this journey are on the way.
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {guides.map((g) => (
              <li key={g.slug}>
                <Link
                  to={`/food-pulse/guides/${g.slug}`}
                  data-testid={`fp-journey-guide-${g.slug}`}
                  className={`group flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 ring-1 ${s.ring} transition hover:border-emerald-300 hover:shadow-sm`}
                >
                  {g.hero_image_url ? (
                    <img
                      src={g.hero_image_url}
                      alt=""
                      className="h-16 w-16 shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-lg ${s.hero}`}>
                      <BookOpen className="h-6 w-6 opacity-60" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <FeatureStatusBadge status={g.status} testid={`fp-journey-guide-status-${g.slug}`} />
                    </div>
                    <h3 className="mt-1 font-serif text-base text-slate-900 group-hover:text-emerald-800">
                      {g.title}
                    </h3>
                    <p className="mt-1 line-clamp-2 text-xs text-slate-600">{g.summary}</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
